import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  ChevronRight, ChevronLeft, ShieldCheck,
  Truck, CreditCard, CheckCircle2, AlertCircle, Mail, Pencil,
} from "lucide-react";
import {
  selectCartItems,
  selectCartTotal,
  selectCartShippingTotal,
  selectCartGrandTotal,
  setCheckoutError,
} from "../store/cartSlice";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── US States ────────────────────────────────────────────────────────
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

// ─── Countries ────────────────────────────────────────────────────────
const COUNTRIES = [
  { code: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { code: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
  { code: "DE", name: "Germany", dial: "+49", flag: "🇩🇪" },
  { code: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
  { code: "IT", name: "Italy", dial: "+39", flag: "🇮🇹" },
  { code: "ES", name: "Spain", dial: "+34", flag: "🇪🇸" },
  { code: "NL", name: "Netherlands", dial: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgium", dial: "+32", flag: "🇧🇪" },
  { code: "CH", name: "Switzerland", dial: "+41", flag: "🇨🇭" },
  { code: "AT", name: "Austria", dial: "+43", flag: "🇦🇹" },
  { code: "SE", name: "Sweden", dial: "+46", flag: "🇸🇪" },
  { code: "NO", name: "Norway", dial: "+47", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", dial: "+45", flag: "🇩🇰" },
  { code: "FI", name: "Finland", dial: "+358", flag: "🇫🇮" },
  { code: "IE", name: "Ireland", dial: "+353", flag: "🇮🇪" },
  { code: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹" },
  { code: "GR", name: "Greece", dial: "+30", flag: "🇬🇷" },
  { code: "PL", name: "Poland", dial: "+48", flag: "🇵🇱" },
  { code: "CZ", name: "Czech Republic", dial: "+420", flag: "🇨🇿" },
  { code: "HU", name: "Hungary", dial: "+36", flag: "🇭🇺" },
  { code: "RO", name: "Romania", dial: "+40", flag: "🇷🇴" },
  { code: "SK", name: "Slovakia", dial: "+421", flag: "🇸🇰" },
  { code: "HR", name: "Croatia", dial: "+385", flag: "🇭🇷" },
  { code: "BG", name: "Bulgaria", dial: "+359", flag: "🇧🇬" },
  { code: "SI", name: "Slovenia", dial: "+386", flag: "🇸🇮" },
  { code: "LT", name: "Lithuania", dial: "+370", flag: "🇱🇹" },
  { code: "LV", name: "Latvia", dial: "+371", flag: "🇱🇻" },
  { code: "EE", name: "Estonia", dial: "+372", flag: "🇪🇪" },
  { code: "NZ", name: "New Zealand", dial: "+64", flag: "🇳🇿" },
  { code: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬" },
  { code: "JP", name: "Japan", dial: "+81", flag: "🇯🇵" },
  { code: "KR", name: "South Korea", dial: "+82", flag: "🇰🇷" },
  { code: "CN", name: "China", dial: "+86", flag: "🇨🇳" },
  { code: "HK", name: "Hong Kong", dial: "+852", flag: "🇭🇰" },
  { code: "TW", name: "Taiwan", dial: "+886", flag: "🇹🇼" },
  { code: "IN", name: "India", dial: "+91", flag: "🇮🇳" },
  { code: "AE", name: "United Arab Emirates", dial: "+971", flag: "🇦🇪" },
  { code: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦" },
  { code: "MX", name: "Mexico", dial: "+52", flag: "🇲🇽" },
  { code: "BR", name: "Brazil", dial: "+55", flag: "🇧🇷" },
  { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷" },
  { code: "CO", name: "Colombia", dial: "+57", flag: "🇨🇴" },
  { code: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦" },
  { code: "NG", name: "Nigeria", dial: "+234", flag: "🇳🇬" },
  { code: "EG", name: "Egypt", dial: "+20", flag: "🇪🇬" },
  { code: "MY", name: "Malaysia", dial: "+60", flag: "🇲🇾" },
  { code: "TH", name: "Thailand", dial: "+66", flag: "🇹🇭" },
  { code: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭" },
  { code: "PK", name: "Pakistan", dial: "+92", flag: "🇵🇰" },
  { code: "UA", name: "Ukraine", dial: "+380", flag: "🇺🇦" },
  { code: "RU", name: "Russia", dial: "+7", flag: "🇷🇺" },
  { code: "TR", name: "Turkey", dial: "+90", flag: "🇹🇷" },
];

const countryByCode = Object.fromEntries(COUNTRIES.map((c) => [c.code, c]));
const dialOf = (code) => countryByCode[code]?.dial || "+1";

const fmtPrice = (v) =>
  (v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_ADDR = {
  first_name:"", last_name:"", company:"",
  line1:"", line2:"", city:"", state:"", postal_code:"",
  country_code:"US", dial_code:"+1", phone:"",
};

const STEPS = ["Shipping", "Billing", "Review"];

// ─── Validation ───────────────────────────────────────────────────────
const PHONE_RE = /^\+?[\d\s\-().]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(addr) {
  const e = {};
  if (!addr.first_name.trim()) e.first_name = "Required";
  if (!addr.last_name.trim())  e.last_name  = "Required";
  if (!addr.line1.trim())      e.line1      = "Required";
  if (!addr.city.trim())       e.city       = "Required";
  if (!addr.state.trim()) e.state = "Please select a state / province";
  if (!addr.postal_code.trim()) e.postal_code = "Zip / postal code is required";
  if (!addr.phone.trim()) {
    e.phone = "Required";
  } else if (!PHONE_RE.test(addr.phone.trim())) {
    e.phone = "Enter a valid phone number (e.g. 555-000-0000)";
  }
  return e;
}

// ─── Shared select arrow SVG ─────────────────────────────────────────
const ARROW_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' fill='none' stroke='%23888' stroke-width='1.5'/%3E%3C/svg%3E")`;

const selectBase = (hasErr) => ({
  width: "100%",
  height: 44,
  borderRadius: 10,
  border: `1.5px solid ${hasErr ? "#fca5a5" : "hsl(var(--border))"}`,
  padding: "0 32px 0 12px",
  fontSize: 14,
  fontFamily: "'Inter', sans-serif",
  background: `hsl(var(--background)) ${ARROW_BG} no-repeat right 10px center`,
  color: "hsl(var(--foreground))",
  outline: "none",
  boxSizing: "border-box",
  cursor: "pointer",
  appearance: "none",
  WebkitAppearance: "none",
});

const inputBase = (hasErr) => ({
  width: "100%",
  height: 44,
  borderRadius: 10,
  border: `1.5px solid ${hasErr ? "#fca5a5" : "hsl(var(--border))"}`,
  padding: "0 12px",
  fontSize: 14,
  fontFamily: "'Inter', sans-serif",
  background: "hsl(var(--background))",
  color: "hsl(var(--foreground))",
  outline: "none",
  boxSizing: "border-box",
});

const LBL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

// ─── Step bar ─────────────────────────────────────────────────────────
function StepBar({ current, sameAsBilling }) {
  const effective = sameAsBilling && current >= 1 ? current + 1 : current;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
        marginBottom: 28,
      }}>
      {STEPS.map((label, i) => {
        const done = i < effective;
        const active = i === effective;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: done
                    ? "#22c55e"
                    : active
                      ? "var(--vb-indigo)"
                      : "hsl(var(--muted))",
                  color:
                    done || active ? "#fff" : "hsl(var(--muted-foreground))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  fontWeight: 700,
                  transition: "all 0.2s",
                }}>
                {done ? <CheckCircle2 size={15} /> : i + 1}
              </div>
              <span
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 10,
                  color: active
                    ? "hsl(var(--foreground))"
                    : "hsl(var(--muted-foreground))",
                  fontWeight: active ? 600 : 400,
                }}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: 48,
                  height: 2,
                  marginBottom: 20,
                  flexShrink: 0,
                  background: done ? "#22c55e" : "hsl(var(--border))",
                  transition: "background 0.3s",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldWrap({ label, error, children }) {
  return (
    <div>
      {label && (
        <label className={LBL} style={{ display: "block", marginBottom: 5 }}>
          {label}
        </label>
      )}
      {children}
      {error && (
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            color: "#dc2626",
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}>
          <AlertCircle size={11} style={{ flexShrink: 0 }} /> {error}
        </p>
      )}
    </div>
  );
}

function TInput({ value, onChange, placeholder, type="text", error, required, readOnly, capitalize }) {
  return (
    <input
      type={type} value={value} onChange={onChange}
      placeholder={placeholder} required={required} readOnly={readOnly}
      autoCapitalize={capitalize ? "words" : undefined}
      style={{ ...inputBase(!!error), ...(readOnly ? { background:"hsl(var(--secondary))", cursor:"default" } : {}), ...(capitalize ? { textTransform: "capitalize" } : {}) }}
      onFocus={e  => { if (!readOnly) e.target.style.borderColor = "var(--vb-indigo)"; }}
      onBlur={e   => { if (!readOnly) e.target.style.borderColor = error ? "#fca5a5" : "hsl(var(--border))"; }}
    />
  );
}

function CountrySelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={selectBase(false)}>
      {COUNTRIES.map((c) => (
        <option key={c.code} value={c.code}>
          {c.flag} {c.name}
        </option>
      ))}
    </select>
  );
}

// US State dropdown — shown when country is US, otherwise free-text
function StateField({ value, onChange, error, countryCode }) {
  if (countryCode === "US") {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={selectBase(!!error)}
        onFocus={(e) => {
          e.target.style.borderColor = "var(--vb-indigo)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? "#fca5a5" : "hsl(var(--border))";
        }}>
        <option value="">Select state…</option>
        {US_STATES.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Province / Region"
      style={inputBase(!!error)}
      onFocus={(e) => {
        e.target.style.borderColor = "var(--vb-indigo)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = error ? "#fca5a5" : "hsl(var(--border))";
      }}
    />
  );
}

function PhoneInput({
  value,
  countryCode,
  onPhoneChange,
  onCountryChange,
  error,
}) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <select
        value={countryCode}
        onChange={(e) =>
          onCountryChange(
            e.target.value,
            countryByCode[e.target.value]?.dial || "+1",
          )
        }
        style={{
          height: 44,
          borderRadius: 10,
          border: `1.5px solid ${error ? "#fca5a5" : "hsl(var(--border))"}`,
          padding: "0 20px 0 8px",
          fontSize: 16,
          cursor: "pointer",
          background: `hsl(var(--background)) ${ARROW_BG} no-repeat right 4px center`,
          appearance: "none",
          WebkitAppearance: "none",
          color: "hsl(var(--foreground))",
          outline: "none",
          width: 80,
          flexShrink: 0,
        }}>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.dial}
          </option>
        ))}
      </select>
      <input
        type="tel"
        value={value}
        placeholder="555 000 0000"
        onChange={(e) => onPhoneChange(e.target.value)}
        style={{ ...inputBase(!!error), flex: 1 }}
        onFocus={(e) => {
          e.target.style.borderColor = "var(--vb-indigo)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? "#fca5a5" : "hsl(var(--border))";
        }}
      />
    </div>
  );
}

function AddressForm({ title, icon, addr, setAddr, errors = {} }) {
  const set = k => e => setAddr(prev => ({ ...prev, [k]: e.target.value }));
  const onCountry = (code) =>
    setAddr((prev) => ({
      ...prev,
      country_code: code,
      dial_code: dialOf(code),
      // Reset state when country changes to avoid stale US state code
      state: "",
    }));

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 20,
        }}>
        {icon}
        <h2
          style={{
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 600,
            fontSize: 18,
            margin: 0,
          }}>
          {title}
        </h2>
      </div>

      {/* Mobile-friendly: single column on small screens, two on larger */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
        }}>
        <FieldWrap label="First Name *" error={errors.first_name}>
          <TInput
            value={addr.first_name}
            onChange={set("first_name")}
            error={errors.first_name}
            required
            capitalize
          />
        </FieldWrap>

        <FieldWrap label="Last Name *" error={errors.last_name}>
          <TInput
            value={addr.last_name}
            onChange={set("last_name")}
            error={errors.last_name}
            required
            capitalize
          />
        </FieldWrap>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldWrap label="Company (optional)">
            <TInput
              value={addr.company}
              onChange={set("company")}
              placeholder="Acme Corp"
            />
          </FieldWrap>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldWrap label="Country *">
            <CountrySelect value={addr.country_code} onChange={onCountry} />
          </FieldWrap>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldWrap label="Address Line 1 *" error={errors.line1}>
            <TInput
              value={addr.line1}
              onChange={set("line1")}
              placeholder="123 Main St"
              error={errors.line1}
              required
            />
          </FieldWrap>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldWrap label="Address Line 2">
            <TInput
              value={addr.line2}
              onChange={set("line2")}
              placeholder="Suite, apt, unit…"
            />
          </FieldWrap>
        </div>

        <FieldWrap label="City *" error={errors.city}>
          <TInput
            value={addr.city}
            onChange={set("city")}
            error={errors.city}
            required
          />
        </FieldWrap>

        <FieldWrap
          label={addr.country_code === "US" ? "State *" : "State / Province *"}
          error={errors.state}>
          <StateField
            value={addr.state}
            onChange={(v) => setAddr((prev) => ({ ...prev, state: v }))}
            error={errors.state}
            countryCode={addr.country_code}
          />
        </FieldWrap>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldWrap label="ZIP / Postal Code *" error={errors.postal_code}>
            <TInput
              value={addr.postal_code}
              onChange={set("postal_code")}
              placeholder="e.g. 48185"
              error={errors.postal_code}
              required
            />
          </FieldWrap>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <FieldWrap label="Phone Number *" error={errors.phone}>
            <PhoneInput
              value={addr.phone}
              countryCode={addr.country_code}
              onPhoneChange={(v) => setAddr((p) => ({ ...p, phone: v }))}
              onCountryChange={(code, dial) =>
                setAddr((p) => ({
                  ...p,
                  country_code: code,
                  dial_code: dial,
                  state: "",
                }))
              }
              error={errors.phone}
            />
          </FieldWrap>
        </div>
      </div>
    </div>
  );
}

// ─── Email confirm banner ─────────────────────────────────────────────
function EmailConfirmBanner({ email, onConfirm, onEdit }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        background: "rgba(91,140,255,0.07)",
        border: "1.5px solid rgba(91,140,255,0.2)",
        borderRadius: 12,
        padding: "12px 16px",
        marginBottom: 20,
      }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <Mail size={15} style={{ color: "var(--vb-indigo)", flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              color: "hsl(var(--muted-foreground))",
              margin: "0 0 1px",
            }}>
            Enter Your Email Please
          </p>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
              color: "hsl(var(--foreground))",
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
            {email}
          </p>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button
          onClick={onEdit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            height: 34,
            padding: "0 12px",
            borderRadius: 8,
            cursor: "pointer",
            border: "1px solid hsl(var(--border))",
            background: "hsl(var(--secondary))",
            fontFamily: "'Inter', sans-serif",
            fontSize: 12,
            fontWeight: 500,
            color: "hsl(var(--foreground))",
          }}>
          <Pencil size={11} /> Edit
        </button>
        <button
          onClick={onConfirm}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            height: 34,
            padding: "0 14px",
            borderRadius: 8,
            cursor: "pointer",
            border: "none",
            background: "var(--vb-indigo)",
            color: "#fff",
            fontFamily: "'Inter', sans-serif",
            fontSize: 12,
            fontWeight: 600,
          }}>
          <CheckCircle2 size={11} /> Looks good
        </button>
      </div>
    </div>
  );
}

// ─── Inline email edit field ──────────────────────────────────────────
function EmailEditField({ value, onChange, onDone, error, saved }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <FieldWrap label="Enter Your Email Please *" error={error}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="your@email.com"
            style={{
              ...inputBase(!!error),
              flex: 1,
              borderColor: saved
                ? "#22c55e"
                : error
                  ? "#fca5a5"
                  : "hsl(var(--border))",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "var(--vb-indigo)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = error
                ? "#fca5a5"
                : saved
                  ? "#22c55e"
                  : "hsl(var(--border))";
            }}
          />
          <button
            onClick={onDone}
            style={{
              height: 44,
              padding: "0 16px",
              borderRadius: 10,
              border: "none",
              background: saved ? "#22c55e" : "var(--vb-indigo)",
              color: "#fff",
              cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "background 0.2s",
            }}>
            {saved ? (
              <>
                <CheckCircle2 size={13} /> Saved
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </FieldWrap>
    </div>
  );
}

function Summary({ items, total, shippingTotal, grandTotal }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.08)",
        padding: "20px 16px",
        position: "sticky",
        top: "calc(var(--header-h, 72px) + 16px)",
      }}>
      <p
        style={{
          fontFamily: "'Oswald', sans-serif",
          fontWeight: 700,
          fontSize: 16,
          marginBottom: 14,
        }}>
        Order Summary
      </p>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 14,
        }}>
        {items.map(
          ({
            product,
            quantity,
            selectedColor,
            shippingOption,
            shippingCost,
          }) => {
            const img = product.photo_urls?.[0] || product.img_url;
            const lineTotal =
              ((product.sale_price || 0) + (shippingCost || 0)) * quantity;
            const shippingLabel =
              shippingOption === "express"
                ? "Express"
                : shippingOption === "sameday"
                  ? "Same-Day"
                  : null;
            return (
              <div
                key={
                  (product.mongo_id || product.id) +
                  (selectedColor || "") +
                  (shippingOption || "")
                }
                style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 8,
                    overflow: "hidden",
                    flexShrink: 0,
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.04)",
                  }}>
                  {img ? (
                    <img
                      src={img}
                      alt={product.product_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "rgba(255,255,255,0.04)",
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                    {product.product_name}
                  </p>
                  {quantity > 1 && (
                    <p
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 10,
                        color: "hsl(var(--muted-foreground))",
                      }}>
                      Qty: {quantity}
                    </p>
                  )}
                  {selectedColor && (
                    <p
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 10,
                        color: "var(--vb-blue)",
                      }}>
                      Color: {selectedColor}
                    </p>
                  )}
                  {shippingLabel && (
                    <p
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 10,
                        color: "var(--vb-blue)",
                        marginTop: 1,
                      }}>
                      🚚 {shippingLabel}
                      {shippingCost > 0 ? ` +${fmtPrice(shippingCost)}` : ""}
                    </p>
                  )}
                </div>
                <span
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--vb-indigo)",
                    flexShrink: 0,
                  }}>
                  ${fmtPrice(lineTotal)}
                </span>
              </div>
            );
          },
        )}
      </div>
      <div
        style={{ borderTop: "1px solid hsl(var(--border))", paddingTop: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5,
          }}>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: "hsl(var(--muted-foreground))",
            }}>
            Subtotal
          </span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
            ${fmtPrice(total)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 10,
          }}>
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: "hsl(var(--muted-foreground))",
            }}>
            Shipping
          </span>
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 12,
              color:
                shippingTotal > 0
                  ? "var(--vb-blue)"
                  : "hsl(var(--muted-foreground))",
            }}>
            {shippingTotal > 0 ? `+${fmtPrice(shippingTotal)}` : "Free"}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid hsl(var(--border))",
            paddingTop: 10,
          }}>
          <span
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              fontSize: 15,
            }}>
            Total
          </span>
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontWeight: 700,
              fontSize: 18,
              color: "var(--vb-indigo)",
            }}>
            ${fmtPrice(grandTotal)}
          </span>
        </div>
      </div>
      <div
        style={{
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "hsl(var(--muted-foreground))",
        }}>
        <ShieldCheck size={13} />
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11 }}>
          Secured by Stripe · SSL encrypted
        </span>
      </div>
    </div>
  );
}

function ReviewBlock({ label, lines }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <p className={LBL} style={{ display: "block", marginBottom: 6 }}>
        {label}
      </p>
      {lines.filter(Boolean).map((l, i) => (
        <p
          key={i}
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 13,
            lineHeight: 1.6,
            margin: 0,
          }}>
          {l}
        </p>
      ))}
    </div>
  );
}

function addrLines(a) {
  const name = `${a.first_name} ${a.last_name}`.trim();
  const country = countryByCode[a.country_code];
  // For US, show full state name if possible
  const stateName =
    a.country_code === "US"
      ? US_STATES.find((s) => s.code === a.state)?.name || a.state
      : a.state;
  return [
    name,
    a.company,
    a.line1,
    a.line2,
    `${a.city}${stateName ? ", " + stateName : ""} ${a.postal_code}`.trim(),
    country?.name,
    a.phone ? `${a.dial_code} ${a.phone}` : "",
  ].filter(Boolean);
}

// ─── Main page ────────────────────────────────────────────────────────
export default function CheckoutPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const items = useSelector(selectCartItems);
  const total = useSelector(selectCartTotal);
  const shippingTotal = useSelector(selectCartShippingTotal);
  const grandTotal = useSelector(selectCartGrandTotal);
  const { user, isAuthenticated } = useSelector((s) => s.auth);

  // ── Email state ────────────────────────────────────────────────────
  const [orderEmail, setOrderEmail] = useState(user?.email || "");
  const [emailConfirmed, setEmailConfirmed] = useState(!isAuthenticated);
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false); // green "Saved" flash
  const [emailError, setEmailError] = useState("");

  // ── Address state ──────────────────────────────────────────────────
  const [step, setStep] = useState(0);
  const [shipping, setShipping] = useState({
    ...EMPTY_ADDR,
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
  });
  const [billing, setBilling] = useState({ ...EMPTY_ADDR });
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shippingErrors, setShippingErrors] = useState({});
  const [billingErrors, setBillingErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState("");

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }

  // ── Email handlers ─────────────────────────────────────────────────
  const handleEmailConfirm = () => {
    setEmailConfirmed(true);
    setEmailEditing(false);
  };

  const handleEmailEdit = () => {
    setEmailEditing(true);
    setEmailConfirmed(false);
    setEmailSaved(false);
  };

  const handleEmailSave = () => {
    if (!orderEmail.trim() || !EMAIL_RE.test(orderEmail.trim())) {
      setEmailError(
        "Please enter a valid email address (e.g. you@example.com)",
      );
      return;
    }
    setEmailError("");
    setEmailSaved(true);
    // Show "Saved" for 1.5s then flip to confirmed pill
    setTimeout(() => {
      setEmailConfirmed(true);
      setEmailEditing(false);
      setEmailSaved(false);
    }, 1200);
  };

  // ── Step navigation ────────────────────────────────────────────────
  const goShippingNext = () => {
    if (!emailConfirmed) {
      setEmailError(
        "Please confirm the email for this order before continuing",
      );
      return;
    }
    const errs = validate(shipping);
    if (Object.keys(errs).length) {
      setShippingErrors(errs);
      return;
    }
    setShippingErrors({});
    if (sameAsBilling) {
      setBilling({ ...shipping });
      setStep(2);
    } else setStep(1);
  };

  const goBillingNext = () => {
    const errs = validate(billing);
    if (Object.keys(errs).length) {
      setBillingErrors(errs);
      return;
    }
    setBillingErrors({});
    setStep(2);
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setGlobalError("");

    const customerName = `${shipping.first_name} ${shipping.last_name}`.trim();

    try {
      const res = await axios.post(`${API}/payments/checkout/cart`, {
        items: items.map((i) => ({
          product_id: i.product.mongo_id || i.product.id,
          quantity: i.quantity,
          shipping_option: i.shippingOption || "free",
          shipping_cost: i.shippingCost || 0,
          ...(i.selectedColor ? { selected_color: i.selectedColor } : {}),
        })),
        origin_url: window.location.origin,
        idempotency_key: crypto.randomUUID(),
        customer_email: orderEmail.trim(),
        customer_name: customerName,
        customer_phone: `${shipping.dial_code} ${shipping.phone}`.trim(),
        user_id: user?.user_id || user?.id || "",
        shipping_name: customerName,
        shipping_line1: shipping.line1,
        shipping_line2: shipping.line2 || "",
        shipping_city: shipping.city,
        shipping_state: shipping.state,
        shipping_postal_code: shipping.postal_code,
        shipping_country: shipping.country_code,
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || "Checkout failed. Please try again.";
      setGlobalError(msg);
      dispatch(setCheckoutError(msg));
    } finally {
      setSubmitting(false);
    }
  }, [items, shipping, orderEmail, user, dispatch]);

  // ── Button style ───────────────────────────────────────────────────
  const btnStyle = (primary) => ({
    height: 48,
    borderRadius: 12,
    padding: "0 24px",
    border: primary ? "none" : "1.5px solid hsl(var(--border))",
    background: primary
      ? submitting
        ? "hsl(var(--muted))"
        : "var(--grad-primary)"
      : "transparent",
    color: primary ? "#fff" : "hsl(var(--foreground))",
    fontFamily: "'Inter', sans-serif",
    fontWeight: 700,
    fontSize: 14,
    cursor: submitting ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    opacity: submitting ? 0.7 : 1,
    transition: "all 0.2s",
    boxShadow: primary ? "0 4px 16px rgba(91,140,255,0.25)" : "none",
  });

  const backAction = () =>
    step > 0
      ? setStep(step === 2 && sameAsBilling ? 0 : step - 1)
      : navigate("/cart");

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div style={{ paddingTop: "var(--header-h, 72px)" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "clamp(16px,4vw,40px) clamp(12px,4vw,24px) 48px",
          }}>
          {/* Back */}
          <button
            onClick={backAction}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "hsl(var(--muted-foreground))",
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              padding: "0 0 20px 0",
            }}>
            <ChevronLeft size={14} /> {step === 0 ? "Back to cart" : "Back"}
          </button>

          <h1
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              marginBottom: 24,
            }}>
            Checkout
          </h1>

          <StepBar current={step} sameAsBilling={sameAsBilling} />

          {/* Responsive: stack on mobile, side-by-side on desktop */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              gap: 20,
            }}
            className="checkout-grid">
            {/* ── Form panel ── */}
            <div
              style={{
                background: "hsl(var(--card))",
                borderRadius: 20,
                border: "1px solid hsl(var(--border))",
                padding: "clamp(16px,4vw,28px)",
              }}>
              {/* ── STEP 0 — Shipping ── */}
              {step === 0 && (
                <>
                  {/* Logged-in: confirm/edit prompt */}
                  {isAuthenticated && !emailEditing && !emailConfirmed && (
                    <EmailConfirmBanner
                      email={orderEmail}
                      onConfirm={handleEmailConfirm}
                      onEdit={handleEmailEdit}
                    />
                  )}

                  {/* Edit mode or guest */}
                  {(emailEditing || !isAuthenticated) && !emailConfirmed && (
                    <EmailEditField
                      value={orderEmail}
                      onChange={(v) => {
                        setOrderEmail(v);
                        setEmailError("");
                        setEmailSaved(false);
                      }}
                      onDone={handleEmailSave}
                      error={emailError}
                      saved={emailSaved}
                    />
                  )}

                  {/* Confirmed email pill */}
                  {emailConfirmed && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        background: "rgba(34,197,94,0.07)",
                        border: "1px solid rgba(34,197,94,0.2)",
                        borderRadius: 10,
                        padding: "8px 14px",
                        marginBottom: 20,
                      }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          minWidth: 0,
                        }}>
                        <CheckCircle2
                          size={14}
                          style={{ color: "#16a34a", flexShrink: 0 }}
                        />
                        <span
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 12,
                            color: "hsl(var(--foreground))",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}>
                          {orderEmail}
                        </span>
                      </div>
                      <button
                        onClick={handleEmailEdit}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--vb-indigo)",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 12,
                          fontWeight: 500,
                          flexShrink: 0,
                        }}>
                        <Pencil size={11} /> Edit
                      </button>
                    </div>
                  )}

                  {emailError && !emailEditing && (
                    <p
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 11,
                        color: "#dc2626",
                        marginBottom: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}>
                      <AlertCircle size={11} /> {emailError}
                    </p>
                  )}

                  <AddressForm
                    title="Shipping Address"
                    icon={
                      <Truck size={18} style={{ color: "var(--vb-indigo)" }} />
                    }
                    addr={shipping}
                    setAddr={setShipping}
                    errors={shippingErrors}
                  />

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginTop: 20,
                      cursor: "pointer",
                      userSelect: "none",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 13,
                    }}>
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => setSameAsBilling(e.target.checked)}
                      style={{
                        width: 17,
                        height: 17,
                        accentColor: "var(--vb-indigo)",
                        cursor: "pointer",
                      }}
                    />
                    Billing address same as shipping
                  </label>

                  <div
                    style={{
                      marginTop: 24,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}>
                    <button style={btnStyle(true)} onClick={goShippingNext}>
                      Continue <ChevronRight size={15} />
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 1 — Billing ── */}
              {step === 1 && (
                <>
                  <AddressForm
                    title="Billing Address"
                    icon={
                      <CreditCard
                        size={18}
                        style={{ color: "var(--vb-indigo)" }}
                      />
                    }
                    addr={billing}
                    setAddr={setBilling}
                    errors={billingErrors}
                  />
                  <div
                    style={{
                      marginTop: 24,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                    }}>
                    <button style={btnStyle(false)} onClick={() => setStep(0)}>
                      <ChevronLeft size={15} /> Back
                    </button>
                    <button style={btnStyle(true)} onClick={goBillingNext}>
                      Continue <ChevronRight size={15} />
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 2 — Review ── */}
              {step === 2 && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 20,
                    }}>
                    <CheckCircle2
                      size={18}
                      style={{ color: "var(--vb-indigo)" }}
                    />
                    <h2
                      style={{
                        fontFamily: "'Oswald', sans-serif",
                        fontWeight: 600,
                        fontSize: 18,
                        margin: 0,
                      }}>
                      Review Your Order
                    </h2>
                  </div>

                  <ReviewBlock label="Order email" lines={[orderEmail]} />
                  <ReviewBlock
                    label="Shipping to"
                    lines={addrLines(shipping)}
                  />
                  <ReviewBlock
                    label="Billing address"
                    lines={
                      sameAsBilling
                        ? ["Same as shipping address"]
                        : addrLines(billing)
                    }
                  />

                  {globalError && (
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fca5a5",
                        borderRadius: 10,
                        padding: "10px 14px",
                        marginBottom: 16,
                        display: "flex",
                        gap: 8,
                        alignItems: "flex-start",
                      }}>
                      <AlertCircle
                        size={14}
                        style={{
                          color: "#dc2626",
                          flexShrink: 0,
                          marginTop: 1,
                        }}
                      />
                      <p
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 12,
                          color: "#dc2626",
                          margin: 0,
                        }}>
                        {globalError}
                      </p>
                    </div>
                  )}

                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 12,
                      color: "hsl(var(--muted-foreground))",
                      lineHeight: 1.6,
                      marginBottom: 16,
                    }}>
                    Clicking "Pay Now" redirects to Stripe's secure checkout.
                    Payment is handled by Stripe.
                  </p>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                    }}>
                    <button
                      style={btnStyle(false)}
                      onClick={() => setStep(sameAsBilling ? 0 : 1)}>
                      <ChevronLeft size={15} /> Back
                    </button>
                    <button
                      style={{
                        ...btnStyle(true),
                        flex: 1,
                        justifyContent: "center",
                      }}
                      onClick={handleSubmit}
                      disabled={submitting}>
                      {submitting ? (
                        "Redirecting…"
                      ) : (
                        <>
                          Pay Now <ChevronRight size={15} />
                        </>
                      )}
                    </button>
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 5,
                      justifyContent: "center",
                    }}>
                    {[
                      "Visa",
                      "Mastercard",
                      "Amex",
                      "Apple Pay",
                      "Google Pay",
                    ].map((n) => (
                      <span key={n} className="payment-badge">
                        {n}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* ── Summary panel ── */}
            <div className="checkout-summary">
              <Summary
                items={items}
                total={total}
                shippingTotal={shippingTotal}
                grandTotal={grandTotal}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Responsive grid: side-by-side on lg screens */}
      <style>{`
        @media (min-width: 960px) {
          .checkout-grid {
            grid-template-columns: 1fr 340px !important;
          }
        }
        @media (max-width: 959px) {
          .checkout-summary {
            order: -1;
          }
        }
      `}</style>

      <SiteFooter />
    </div>
  );
}
